import React, { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import moment from "moment";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { addParcelQuote, editManualQuote, getCurrencies, addManualQuote, editParcelQuote } from "api";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import TimeInput from "components/atoms/TimeInput/TimeInput";

import FormField from "components/molecules/FormField/FormField";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";
import SearchSelect from "../../RequestForm/components/Common/SearchSelect";

function ManualParcelQuoteDialog({
  isOpen,
  close,
  providerId,
  shipmentId,
  editManualQuoteData,
  fetchQuoteRequestData,
  isParcel,
  isManualQuote,
  refetchQuotes,
}) {
  const intl = useIntl();
  const methods = useForm({ shouldFocusError: true });
  const { getValues, handleSubmit, reset } = methods;

  const [isLoading, setIsLoading] = useState(false);
  const [customInfoCurrencyOption, setCustomInfoCurrencyOption] = useState([
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.pleaseSelect",
        defaultMessage: "Please select",
      }),
      value: "",
      id: null,
    },
  ]);

  const formatDate = (date) => moment(date).format("YYYY-MM-DD");
  const formatTime = (date) => moment(date).format("HH:mm");

  const prefillEditData = () => {
    reset({
      tracking_number: editManualQuoteData.tracking_number,
      pickup_date: formatDate(editManualQuoteData.pickup_date),
      pickup_time: formatTime(editManualQuoteData.pickup_time),
      delivery_date: formatDate(editManualQuoteData.delivery_date),
      delivery_time: formatTime(editManualQuoteData.delivery_time),
      total_cost: editManualQuoteData.value,
      currency_name: editManualQuoteData.currency_code,
    });
  };

  const handleCloseDialog = () => {
    reset();
    close();
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    const formattedData = {
      ...(providerId && isParcel && { parcel_provider: providerId }), // Only applies to new parcel quote, not edit quote.
      ...(providerId && isManualQuote && { manual_provider: providerId }), // Only applies to new manual quote, not edit quote.
      tracking_number: data.tracking_number,
      pickup_date: data.pickup_date,
      pickup_time: data.pickup_time,
      delivery_date: data.delivery_date,
      delivery_time: data.delivery_time,
      value: Number(data.total_cost),
      currency_code: data.currency || getValues("currency_name"),
    };

    if (editManualQuoteData && !shipmentId) {
      const editQuoteFn = isParcel ? editParcelQuote : editManualQuote;
      editQuoteFn(editManualQuoteData.reference, formattedData)
        .then((res) => {
          toast(res.data.detail, { type: "success" });
          fetchQuoteRequestData();
          handleCloseDialog();
        })
        .catch((err) =>
          errorToast(
            err,
            intl.formatMessage({
              id: "shipment-quotes.components.quotes.couldNotEditQuote",
              defaultMessage: "Could not edit quote, please try again later",
            }),
          ),
        )
        .finally(() => setIsLoading(false));
      return;
    }

    if (isParcel) {
      addParcelQuote(shipmentId, formattedData)
        .then((res) => {
          if (res.status === 201) {
            refetchQuotes();
            handleCloseDialog();
          }
        })
        .catch((err) => {
          errorToast(
            err,
            intl.formatMessage({
              id: "shipment-quotes.components.quotes.couldNotCreateParcelQuote",
              defaultMessage: "Could not create manual parcel quote, please try again later",
            }),
          );
        })
        .finally(() => setIsLoading(false));
      return;
    }
    if (isManualQuote) {
      addManualQuote(shipmentId, formattedData)
        .then((res) => {
          if (res.status === 201) {
            refetchQuotes();
            handleCloseDialog();
          }
        })
        .catch((err) => {
          errorToast(
            err,
            intl.formatMessage({
              id: "shipment-quotes.components.quotes.couldNotCreateParcelQuote",
              defaultMessage: "Could not create manual quote, please try again later",
            }),
          );
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (!editManualQuoteData || Object.keys(editManualQuoteData).length === 0) return;
    prefillEditData();
  }, [editManualQuoteData]);

  useEffect(() => {
    if (!isOpen) return;
    reset();
    getCurrencies()
      .then((res) => {
        if (res.data) setCustomInfoCurrencyOption(res.data);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.packing-list.couldNotRetrieveCurrencies",
            defaultMessage: "Could not retrieve currencies. Please try again later.",
          }),
        ),
      );
  }, [isOpen]);

  return (
    <Dialog size="sm" title={isParcel ? "Parcel Rate and ETA" : "Manual quote"} show={isOpen} onHide={close}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3">
            <p className="font-weight-bold mb-2">
              <FormattedMessage id="quote-request.trackingNumber" defaultMessage="Tracking number" />
            </p>
            <div className="input-element w-192">
              <FormField type="text" field_id="tracking_number" eventContext="Manual Parcel Quote Dialog" />
            </div>
          </div>
          <div className="mb-3">
            <p className="font-weight-bold mb-2 required">
              <FormattedMessage id="general.pickUpDateAndTime" defaultMessage="Pickup Date and Time" />
            </p>
            <Row>
              <Col className="d-flex">
                <DatePicker
                  minDate={new Date()}
                  eventContext="Manual Parcel Quote Dialog"
                  name="pickup_date"
                  wrapperClassNames="mr-2"
                  required
                />
                <TimeInput
                  eventContext="Manual Parcel Quote Dialog"
                  name="pickup_time"
                  outputFormat="HH:mm"
                  required
                  defaultValue="09:00"
                />
              </Col>
            </Row>
          </div>
          <div className="mb-3">
            <p className="font-weight-bold mb-2 required">
              <FormattedMessage id="general.deliveryDateAndTime" defaultMessage="Delivery Date and Time" />
            </p>
            <Row>
              <Col className="d-flex">
                <DatePicker
                  eventContext="Manual Parcel Quote Dialog"
                  name="delivery_date"
                  wrapperClassNames="mr-2"
                  required
                  minDate={new Date()}
                />
                <TimeInput
                  eventContext="Manual Parcel Quote Dialog"
                  outputFormat="HH:mm"
                  name="delivery_time"
                  required
                  defaultValue="17:00"
                />
              </Col>
            </Row>
          </div>
          <div style={{ marginBottom: "25px" }}>
            <p className="font-weight-bold mb-2 required">
              <FormattedMessage id="general.totalCosts" defaultMessage="Total Costs" />
            </p>
            <Row>
              <Col className="d-flex">
                <div className="input-element w-192 mr-2">
                  <FormField mandatory onlyNumber field_id="total_cost" eventContext="Manual Parcel Quote Dialog" />
                </div>

                <div className="w-192 mr-8">
                  <SearchSelect
                    options={customInfoCurrencyOption.map((currency) => ({
                      value: currency.code,
                      label: `${currency.name} (${currency.code})`,
                    }))}
                    name="currency_name"
                    required
                    placeholder="Currency"
                  />
                </div>
              </Col>
            </Row>
          </div>

          <Button
            size="md"
            type="submit"
            variant="primary"
            label={editManualQuoteData && !shipmentId ? `Edit Quote` : `Add ${isParcel ? "Parcel" : "Manual"} Quote`}
            className="ml-auto mt-3"
            isLoading={isLoading}
          />
        </form>
      </FormProvider>
    </Dialog>
  );
}

ManualParcelQuoteDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  shipmentId: PropTypes.string,
  fetchQuoteRequestData: PropTypes.func,
  editManualQuoteData: PropTypes.instanceOf(Object),
  providerId: PropTypes.number,
  isParcel: PropTypes.bool,
  isManualQuote: PropTypes.bool.isRequired,
  refetchQuotes: PropTypes.func,
};

ManualParcelQuoteDialog.defaultProps = {
  shipmentId: "",
  fetchQuoteRequestData: () => {},
  editManualQuoteData: {},
  isParcel: false,
  providerId: null,
  refetchQuotes: () => {},
};

export default ManualParcelQuoteDialog;
